import React, {useEffect, useState} from 'react';
import Input from "../Common/Input";
import ImageUpload from "../Common/File/ImageUpload";
import Button from "../Common/Button";
import {FIELD_BUSINESS, VARIANT} from "../../constants";
import './style.css';
import {checkUpload, scrollToTop, validatePhone} from "../../utils";
import NewSelect from "../Common/Select/NewSelect";
import {helperUpload} from "../../network/api";
import locationStore from "../../store/locationStore";
import merchantStore from "../../store/merchantStore";
import Avatar from "../Avatar";
import IMG from "../../constants/image";
import Textarea from "../Common/Input/Textarea";
import {isEmpty} from "lodash";
import {text} from "../../constants/text";

const StoreForm = ({editing, onboarding, setEditing, onUpdateData}) => {
  const {provinces, districts, communes, getProvinces, getDistricts, getCommunes} = locationStore()
  const goodFields = FIELD_BUSINESS.map(item => ({...item, id: item.name}));
  const {merchantProfile} = merchantStore();
  const {
    address,
    attachments,
    avatar_url,
    description,
    district,
    email,
    goods_industries,
    owner_name,
    merchant_name,
    phone_number,
    province,
    commune
  } = merchantProfile || {}

  const [formData, setFormData] = useState({
    merchantName: onboarding ? '' : merchant_name || '',
    ownerName: onboarding ? '' : owner_name || '',
    description: onboarding ? '' : description || '',
    phoneNumber: onboarding ? '' : phone_number || '',
    email: onboarding ? '' : email || '',
    address: onboarding ? '' : address || '',
    province: onboarding ? '' : province || '',
    district: onboarding ? '' : district || '',
    commune: onboarding ? '' : commune || '',
    goodsIndustries: onboarding ? '' : goods_industries?.[0] || '',
  });

  const [documents, setDocuments] = useState(onboarding ? [] : attachments || []);
  const [avatar, setAvatar] = useState(avatar_url);
  const [isUpdateAvatar, setIsUpdateAvatar] = useState(false);
  const [errors, setErrors] = useState({
    merchantName: '',
    ownerName: '',
    description: '',
    phoneNumber: '',
    email: '',
    address: '',
    province: '',
    district: '',
    commune: '',
    goodsIndustries: '',
  });

  useEffect(() => {
    getProvinces();
  }, []);

  useEffect(() => {
    setAvatar(avatar_url)
    setIsUpdateAvatar(true);
  }, [avatar_url]);

  useEffect(() => {
    if (merchantProfile && !onboarding) {
      setFormData({
        merchantName: merchant_name || '',
        ownerName: owner_name || '',
        description: description || '',
        phoneNumber: phone_number || '',
        email: email || '',
        address: address || '',
        province: province || '',
        district: district || '',
        commune: commune || '',
        goodsIndustries: goods_industries?.[0] || '',
      })
      setDocuments(attachments || []);
    }
  }, [merchantProfile, onboarding]);

  useEffect(() => {
    if (!formData?.province || !editing || isEmpty(provinces)) return;

    const provinceValue = formData.province?.name || formData.province;
    const matchedProvince = provinces?.find(pr =>
      pr.id === provinceValue || pr.fullName === provinceValue
    );

    if (matchedProvince?.code) {
      getDistricts({provinceId: matchedProvince.code});
    }
  }, [formData?.province, editing, provinces]);

  useEffect(() => {
    if (!districts?.length || !formData?.district || !editing) return;

    const districtValue = String(formData.district?.name ?? formData.district ?? '').toLowerCase();
    const matchedDistrict = districts.find(dis =>
      String(dis.id ?? '').toLowerCase() === districtValue ||
      String(dis.fullName ?? '').toLowerCase() === districtValue ||
      String(dis.code ?? '').toLowerCase() === districtValue
    );
    if (matchedDistrict?.code) {
      getCommunes({districtId: matchedDistrict.code});
    }
  }, [districts, formData?.district, editing]);

  const validateForm = () => {
    const newErrors = {};
    let valid = true;

    if (!formData.merchantName) {
      newErrors.merchantName = text.storeNameRequired;
      valid = false;
    }

    if (!formData.ownerName) {
      newErrors.ownerName = text.representativeNameRequired;
      valid = false;
    }

    if (!formData.phoneNumber) {
      newErrors.phoneNumber = text.phoneNumberRequired;
      valid = false;
    } else {
      if (!validatePhone(formData.phoneNumber)) {
        newErrors.phoneNumber = text.invalidPhoneNumber;
        valid = false;
      }
    }

    if (!formData.email) {
      newErrors.email = text.emailRequired;
      valid = false;
    } else {
      const emailValue = String(formData.email || '').trim();
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(emailValue)) {
        newErrors.email = text.invalidEmail;
        valid = false;
      }
    }

    if (!formData.address) {
      newErrors.address = text.businessAddressRequired;
      valid = false;
    }

    if (!formData.province) {
      newErrors.province = text.provinceCityRequired;
      valid = false;
    }

    if (!formData.district) {
      newErrors.district = text.districtRequired;
      valid = false;
    }

    if (!formData.commune) {
      newErrors.commune = text.wardCommuneRequired;
      valid = false;
    }

    if (!formData.goodsIndustries) {
      newErrors.goodsIndustries = text.businessCategoryRequired;
      valid = false;
    }

    if (!valid) {
      scrollToTop();
    }
    setErrors(newErrors);
    return valid;
  };

  const handleChange = (e) => {
    const {name, value, code} = e.target;
    setErrors({...errors, [name]: ''}); // Clear the error when user starts typing
    if (name === 'province') {
      setFormData({...formData, [name]: onboarding ? {name: value, code} : value, district: '', commune: ''});
    } else if (name === 'district') {
      setFormData({...formData, [name]: onboarding ? {name: value, code} : value, commune: ''});
    } else if (name === 'commune' && onboarding) {
      setFormData({...formData, [name]: {name: value, code}});
    } else {
      setFormData({...formData, [name]: value});
    }
  };

  const handleEditInfo = async () => {
    scrollToTop();
    setEditing && setEditing();
  }

  const handleSubmit = async () => {
    if (validateForm()) {
      const newFormData = {...formData};
      // Proceed with form submission logic
      if (isUpdateAvatar) {
        const needUploadFiles = checkUpload(documents);
        if (needUploadFiles?.length > 0) {
          const uploadData = await helperUpload({files: needUploadFiles});
          if (uploadData?.success) {
            newFormData.uploadedAttachments = uploadData?.data?.map(item => item?.key);
          }
        } else {
          newFormData.uploadedAttachments = documents?.map((item) => item?.id);
        }
      }

      if (avatar && typeof avatar === 'object' && isUpdateAvatar) {
        const uploadAvatar = await helperUpload({files: [avatar]});
        if (uploadAvatar?.success) {
          newFormData.avatarUrl = uploadAvatar?.data?.[0]?.url;
        }
      }

      newFormData.goodsIndustries = [formData?.goodsIndustries]
      updateData({newFormData});
    }
  };

  const updateData = async ({newFormData}) => {
    onUpdateData && onUpdateData({newFormData})
  }

  const handleFileUploadAvatar = (avt) => {
    setAvatar(avt);
  }

  const handleFileUpload = (files) => {
    setDocuments(files);
  };

  return (
    <>
      <form className="flex flex-col gap-5 justify-center pb-[100px]" onSubmit={handleSubmit}>
        {
          !onboarding && (
            <div className="store-section">
              <div>
                <p className="text-sm mb-2">{text.profilePicture}</p>
                <Avatar
                  onUpload={handleFileUploadAvatar}
                  image={avatar}
                  viewOnly={!editing}
                />
              </div>
              <Textarea
                warningIcon={!formData.description && IMG.icon.exclamation}
                title={text.storeDescription}
                placeholder={text.enterStoreName}
                name="description"
                value={!editing ? formData.description || text.storeDescriptionEmptyPrompt : formData.description}
                onChange={handleChange}
                viewOnly={!editing}
              />
            </div>
          )
        }

        <div className="store-section">
          <div>
            <Input
              required
              title={text.storeName}
              placeholder={text.enterStoreName}
              name="merchantName"
              error={errors.merchantName}
              value={formData.merchantName}
              onChange={handleChange}
              viewOnly={!editing}
            />
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <Input
              required
              title={text.representativeName}
              placeholder={text.representativeName}
              name="ownerName"
              error={errors.ownerName}
              value={formData.ownerName}
              onChange={handleChange}
              viewOnly={!editing}
            />
            <Input
              required
              type='number'
              title={text.phoneContact}
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder={text.phone}
              name="phoneNumber"
              error={errors.phoneNumber}
              value={formData.phoneNumber}
              onChange={handleChange}
              viewOnly={!editing}
            />
          </div>
          <div>
            <Input
              required
              type="email"
              title={text.email}
              placeholder={text.enterEmail}
              name="email"
              error={errors.email}
              className={errors.email ? 'mb-6' : ''}
              value={formData.email}
              onChange={handleChange}
              viewOnly={!editing}
            />
            <p className="mt-1 text-xs text-[#E4626F]">{text.storeEmailNote}</p>
          </div>
        </div>

        <div className="store-section">
          <div>
            <Input
              required
              title={text.businessAddress}
              placeholder={text.streetAddress}
              name="address"
              error={errors.address}
              value={formData.address}
              onChange={handleChange}
              viewOnly={!editing}
            />
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            <NewSelect
              isAddress
              required
              data={provinces}
              title={text.provinceCity}
              placeholder={text.pleaseSelect}
              name="province"
              error={errors.province?.name}
              value={formData?.province?.name || formData?.province}
              onChange={handleChange}
              viewOnly={!editing}
            />
            <NewSelect
              isAddress
              required
              data={districts}
              title={text.districtCounty}
              placeholder={text.pleaseSelect}
              name="district"
              error={errors.district?.name}
              value={formData?.district?.name || formData?.district}
              onChange={handleChange}
              viewOnly={!editing}
            />
            <NewSelect
              isAddress
              required
              data={communes}
              title={text.wardCommune}
              placeholder={text.pleaseSelect}
              name="commune"
              error={errors.commune?.name}
              value={formData?.commune?.name || formData?.commune}
              onChange={handleChange}
              viewOnly={!editing}
            />
          </div>
        </div>

        <div className="store-section">
          <NewSelect
            className="md:w-[320px]"
            required
            data={goodFields}
            title={text.businessSector}
            placeholder={text.pleaseSelect}
            name="goodsIndustries"
            error={errors.goodsIndustries}
            value={formData.goodsIndustries}
            onChange={handleChange}
            viewOnly={!editing}
          />
        </div>

        <div className="store-section">
          <p className="text-sm">{text.otherDocs}</p>
          <ImageUpload
            onUpload={handleFileUpload}
            images={documents}
            viewOnly={!editing}
            isDocOther
          />
        </div>
        <div className='w-full'>
          {
            onboarding ?
              <Button className='mx-auto min-w-[285px]' variant={VARIANT.PRIMARY} onClick={handleSubmit}>
                {text.completeAndCreateProduct}
              </Button> :
              editing ?
                <Button className='mx-auto min-w-[285px]' variant={VARIANT.PRIMARY}
                        onClick={handleSubmit}>
                  {text.updateInfo}
                </Button> :
                <Button className='mx-auto min-w-[285px]' variant={VARIANT.PRIMARY}
                        onClick={handleEditInfo}>
                  {text.editInfo}
                </Button>
          }
        </div>
      </form>

    </>
  );
}

export default StoreForm;
